import { Controller } from "@hotwired/stimulus"
import { projectRange, dollars, roundedDollars } from "../lib/projection"

// Landing DCA calculator: amount + years sliders and a mix toggle drive a live
// forecast range. The hero packet uses it without the range, the put-in line,
// the bar and the years slider (those targets are optional there: a plan
// runs until it is stopped, so the packet projects a fixed 15 years), and
// takes its mix from the packet controller's packet:mix event instead of its
// own chips. The math
// lives in lib/projection.js, which mirrors app/models/projection.rb, so the
// marketing preview matches what a real plan projects.
export default class extends Controller {
  static targets = [
    "amount", "years", "amountOut", "yearsOut",
    "mid", "low", "high", "contributed", "putInBar", "growthBar", "mixBtn", "pct"
  ]
  static values = {
    bands: Object,   // Projection::BANDS
    presets: Object, // RiskQuiz::PRESETS
    mix: { type: String, default: "balanced" },
    years: { type: Number, default: 15 } // when the page has no years slider
  }

  connect() {
    this.syncMixButtons()
    this.update()
  }

  selectMix(event) {
    this.mixValue = event.params.mix
    this.syncMixButtons()
    this.update()
  }

  mixChanged({ detail: { mix } }) {
    this.mixValue = mix
    this.update()
  }

  syncMixButtons() {
    this.mixBtnTargets.forEach((btn) => {
      const active = btn.dataset.mix === this.mixValue
      btn.dataset.active = String(active)
      btn.setAttribute("aria-pressed", String(active))
    })
  }

  update() {
    const amount = Number(this.amountTarget.value)
    const years = this.hasYearsTarget ? Number(this.yearsTarget.value) : this.yearsValue
    const months = years * 12
    const split = this.presetsValue[this.mixValue]

    this.amountOutTarget.textContent = dollars(amount)
    if (this.hasYearsOutTarget) this.yearsOutTarget.textContent = years
    // Screen readers get the same words the sighted reader sees beside each slider.
    this.amountTarget.setAttribute("aria-valuetext", `${dollars(amount)} a month`)
    if (this.hasYearsTarget) this.yearsTarget.setAttribute("aria-valuetext", `${years} ${years === 1 ? "year" : "years"}`)

    const { low, high, mid, contributed } = projectRange({
      amount, months, bands: this.bandsValue, split, denom: 100
    })
    if (this.hasContributedTarget) this.contributedTarget.textContent = this.big(contributed)

    // Lead with the middle of the range — an honest "typical" outcome, never
    // the best case — and keep the full range as a quiet note.
    this.midTarget.textContent = this.big(mid)
    if (this.hasLowTarget) this.lowTarget.textContent = this.big(low)
    if (this.hasHighTarget) this.highTarget.textContent = this.big(high)

    // Possible growth on the middle estimate, as a quiet companion to the
    // headline. Total over the chosen years — the slider right beside it
    // names the span, so it can't pass for an annual rate.
    const growthPct = contributed > 0 ? ((mid - contributed) / contributed) * 100 : 0
    this.pctTarget.textContent = growthPct >= 1 ? `+${Math.round(growthPct)}%` : ""

    // Bar: how much of the middle estimate is your own money vs. growth.
    if (!this.hasPutInBarTarget) return
    const putInPct = Math.min(100, (contributed / mid) * 100)
    this.putInBarTarget.style.width = `${putInPct}%`
    this.growthBarTarget.style.width = `${100 - putInPct}%`
  }

  // Rounded to clean human numbers (lib/projection.js#roundedDollars).
  big(value) {
    return roundedDollars(value)
  }
}
